import { useState } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle, Separator, Tooltip, TooltipContent, TooltipTrigger } from '@cuspar/ui';
import axios from 'axios';
import { wrapAxios } from '../axios';
import { DomainSearchForm } from '../components/Forms/DomainSearchForm';
import { IWhoisFormatted } from '@cuspar/ywt.ch.model';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const WhoisPage = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [whois, setWhois] = useState<IWhoisFormatted | null>(null);
  const [lastSearchedDomain, setLastSearchedDomain] = useState<string | null>(null);

  async function onDomainSearch({ domain }: { domain: string }) {
    console.log('onDomainSearch', domain);
    setIsLoading(true);
    setWhois(null);
    setLastSearchedDomain(domain);

    const response = await wrapAxios<IWhoisFormatted>(() => axios.get('/whois/domain', { params: { domain } }));
    setIsLoading(false);

    if (!response.success) return;

    setWhois(response.data ?? null);
  }

  return (
    <>
      <h1 className='font-mono text-center text-2xl md:text-4xl font-bold leading-tight tracking-tighter lg:leading-[1.1] mb-2'>{t('whois.title')}</h1>
      <h2 className='text-center text-sm text-muted-foreground mb-10'>{t('whois.subtitle')}</h2>

      <DomainSearchForm keepTld className='container max-w-3xl mx-auto' onSubmit={onDomainSearch} placeholder={t('whois.placeholder')}>
        <Button loading={isLoading} size='sm'>
          {t('whois.check')}
        </Button>
      </DomainSearchForm>

      <div className='container max-w-3xl mx-auto mt-8 space-y-4'>
        {!isLoading && lastSearchedDomain && !whois && <p className='text-sm text-muted-foreground text-center'>{t('whois.noData')}</p>}

        {whois && (
          <Card className='border bg-card/60 backdrop-blur'>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className='w-4 h-4 inline-block absolute top-3 right-3 text-muted-foreground/60 hover:text-muted-foreground' />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('whois.disclaimer')}</p>
                <p>{t('whois.disclaimerTld')}</p>
              </TooltipContent>
            </Tooltip>
            <CardHeader>
              <CardTitle className='text-sm'>
                {t('whois.summaryFor')} <span className='font-mono break-all'>{whois.domain?.domain ?? lastSearchedDomain}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4 text-xs'>
              {whois.domain && (
                <section className='space-y-1'>
                  <p>
                    <span className='font-medium'>{t('whois.domain')}</span> <span className='text-foreground font-bold'>{whois.domain?.domain ?? lastSearchedDomain}</span>
                  </p>

                  <div className='grid gap-1'>
                    {whois.domain.status && whois.domain.status.length > 0 && (
                      <p className='inline-flex items-start gap-2'>
                        <span className='font-medium'>{t('whois.status')}</span>{' '}
                        <ul className='list-inside'>
                          {whois.domain.status.map((status) => (
                            <li key={status}>{status}</li>
                          ))}
                        </ul>
                      </p>
                    )}
                    {whois.domain.name_servers && whois.domain.name_servers.length > 0 && (
                      <p className='inline-flex items-start gap-2'>
                        <span className='font-medium'>{t('whois.nameServers')}</span>{' '}
                        <ul className='list-inside'>
                          {whois.domain.name_servers.map((ns) => (
                            <li key={ns}>{ns}</li>
                          ))}
                        </ul>
                      </p>
                    )}
                    {whois.domain.created_date && (
                      <p>
                        <span className='font-medium'>{t('whois.created')}</span> {whois.domain.created_date}
                      </p>
                    )}
                    {whois.domain.expired_date && (
                      <p>
                        <span className='font-medium'>{t('whois.expires')}</span> {whois.domain.expired_date}
                      </p>
                    )}
                    {whois.domain.updated_date && (
                      <p>
                        <span className='font-medium'>{t('whois.updated')}</span> {whois.domain.updated_date}
                      </p>
                    )}
                    {whois.domain.whois_server && (
                      <p>
                        <span className='font-medium'>{t('whois.whoisServer')}</span> {whois.domain.whois_server}
                      </p>
                    )}
                    {whois.domain.dnssec && (
                      <p>
                        <span className='font-medium'>{t('whois.dnssec')}</span> {whois.domain.dnssec ? t('common.yes') : t('common.no')}
                      </p>
                    )}
                  </div>
                </section>
              )}

              {(whois.registrar || whois.registrant || whois.administrative || whois.technical || whois.billing) && (
                <>
                  <Separator className='my-2' />
                  <section className='grid gap-4 md:grid-cols-2'>
                    {whois.registrar && (
                      <div className='space-y-1'>
                        <h2 className='font-semibold text-sm'>{t('whois.registrar')}</h2>
                        {whois.registrar.registrar_name && (
                          <p>
                            <span className='font-medium'>{t('whois.registrarName')}</span> {whois.registrar.registrar_name}
                          </p>
                        )}
                        {whois.registrar.referral_url && (
                          <p className='break-all'>
                            <span className='font-medium'>{t('whois.registrarWebsite')}</span> {whois.registrar.referral_url}
                          </p>
                        )}
                        {whois.registrar.registrar_email && (
                          <p className='break-all'>
                            <span className='font-medium'>{t('whois.registrarEmail')}</span> {whois.registrar.registrar_email}
                          </p>
                        )}
                        {whois.registrar.registrar_phone && (
                          <p>
                            <span className='font-medium'>{t('whois.registrarPhone')}</span> {whois.registrar.registrar_phone}
                          </p>
                        )}
                      </div>
                    )}

                    {whois.registrant && (
                      <div className='space-y-1'>
                        <h2 className='font-semibold text-sm'>{t('whois.registrant')}</h2>
                      </div>
                    )}

                    {whois.administrative && (
                      <div className='space-y-1'>
                        <h2 className='font-semibold text-sm'>{t('whois.administrative')}</h2>
                      </div>
                    )}

                    {whois.technical && (
                      <div className='space-y-1'>
                        <h2 className='font-semibold text-sm'>{t('whois.technical')}</h2>
                      </div>
                    )}

                    {whois.billing && (
                      <div className='space-y-1'>
                        <h2 className='font-semibold text-sm'>{t('whois.billing')}</h2>
                      </div>
                    )}
                  </section>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};
